// MarketSignalRenderer.tsx
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { ExternalLink } from '@/components/ui/lucide-memo';
import type { BaseEntityProps, EntityRowAction, LeasingMarketSignalDTO } from '#types/types';
import { useKernelStore } from '@/kernel/kernelStores';
import { formatMarketSignalRent, formatMarketSignalSurface } from '@/lib/leasing/marketSignalDisplay';
import { portalCard } from '@/components/portal/portalTheme';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';

const translations = {
  en: {
    unknown: 'Unknown',
    web: 'Web',
    retailType: 'Retail',
    notAvailable: '-',
  },
  fr: {
    unknown: 'Inconnu',
    web: 'Web',
    retailType: 'Retail',
    notAvailable: '-',
  },
};

export interface MarketSignalRendererProps extends BaseEntityProps<LeasingMarketSignalDTO> {
  isExpanded?: boolean;
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingMarketSignalDTO>[];
}

const DEFAULT_MARKET_SIGNAL_COLUMNS: TableColumnDef[] = [
  { id: 'listing', header: '', minWidth: 250 },
  { id: 'location', header: '', minWidth: 180 },
  { id: 'surface', header: '', minWidth: 100 },
  { id: 'rent', header: '', minWidth: 120 },
  { id: 'source', header: '', minWidth: 100 },
  { id: 'status', header: '', minWidth: 100 },
  { id: 'actions', header: '', minWidth: 100, align: 'right' },
];

const EMPTY_ROW_ACTIONS: EntityRowAction<LeasingMarketSignalDTO>[] = [];

const CELL_STYLE_START: React.CSSProperties = { justifyContent: 'flex-start' };
const CELL_STYLE_CENTER: React.CSSProperties = { justifyContent: 'center' };
const CELL_STYLE_END: React.CSSProperties = { justifyContent: 'flex-end' };

const getCellStyle = (column: TableColumnDef): React.CSSProperties => {
  if (column.id === 'actions' || column.align === 'right') return CELL_STYLE_END;
  if (column.align === 'center') return CELL_STYLE_CENTER;
  return CELL_STYLE_START;
};

export const MarketSignalRenderer = React.memo(({
  entityId,
  variant = 'card',
  onClick,
  className,
  isExpanded,
  columns,
  rowActions = EMPTY_ROW_ACTIONS,
}: MarketSignalRendererProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const normalizedVariant = variant === 'page' ? 'detail' : variant;
  const data = useKernelStore((state) => state.inventory.MarketSignal.registry[entityId]) as LeasingMarketSignalDTO | undefined;

  const handleClick = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      if (onClick) onClick();
    },
    [onClick]
  );

  const handleOpenListing = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!data?.listingUrl) return;
      window.open(data.listingUrl, '_blank', 'noopener,noreferrer');
    },
    [data?.listingUrl]
  );

  if (!data) return <EntitySkeleton variant={normalizedVariant} className={className} />;

  switch (normalizedVariant) {
    case 'row': {
      const cellRenderers: Record<string, React.ReactNode> = {
        listing: (
          <div className="min-w-0 h-full flex flex-col justify-center">
            <div className="truncate font-medium text-white" title={data.propertyName || ''}>
              {data.propertyName || data.unitIdentifier || t.notAvailable}
            </div>
            <div className="truncate text-[10px] text-white/40">{data.unitType || t.retailType}</div>
          </div>
        ),
        location: (
          <div className="truncate text-xs text-white/70 h-full flex items-center">
            {data.city}
            {data.postalCode ? ` (${data.postalCode})` : ''}
          </div>
        ),
        surface: (
          <div className="text-sm font-mono text-white/90 h-full flex items-center">
            {formatMarketSignalSurface(data)}
          </div>
        ),
        rent: (
          <div className="text-sm font-mono text-white/90 h-full flex items-center">
            {formatMarketSignalRent(data, lang === 'fr' ? 'fr-FR' : 'en-US')}
          </div>
        ),
        source: (
          <div className="truncate text-xs text-white/60 h-full flex items-center">
            {data.sourceName || t.web}
          </div>
        ),
        status: (
          <div className="h-full flex items-center">
            <span
              className={clsx(
                'rounded-full border px-2 py-0.5 text-[10px] font-bold',
                data.status === 'AVAILABLE'
                  ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
                  : 'border-white/10 bg-white/5 text-white/40'
              )}
            >
              {data.status || t.unknown}
            </span>
          </div>
        ),
        actions: (
          <div className="flex items-center justify-end gap-2 h-full">
            <EntityRowActions entityType="MarketSignal" entity={data} rowActions={rowActions} />
            {data.listingUrl && (
              <button
                type="button"
                onClick={handleOpenListing}
                className="rounded p-1 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
                title="Open listing"
              >
                <ExternalLink size={12} />
              </button>
            )}
          </div>
        ),
      };

      const activeColumns = columns ?? DEFAULT_MARKET_SIGNAL_COLUMNS;

      return (
        <div
          className={clsx(
            styles.gridRow,
            styles.bodyRow,
            'group h-10 w-full cursor-pointer border-b border-[#2A2A2A]',
            isExpanded ? 'bg-[#222222]' : 'bg-[#181818] hover:bg-[#1E1E1E]',
            className
          )}
          onClick={handleClick}
        >
          {activeColumns.map((column) => (
            <div
              key={column.id}
              className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
              style={getCellStyle(column)}
            >
              {cellRenderers[column.id] || null}
            </div>
          ))}
        </div>
      );
    }
    case 'chip':
      return (
        <button
          type="button"
          onClick={handleClick}
          className={clsx(
            'inline-flex items-center rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/80 hover:bg-white/10',
            className
          )}
        >
          {data.propertyName || data.unitIdentifier || t.notAvailable}
        </button>
      );
    case 'card':
      return (
        <div className={clsx(portalCard, 'p-4', className)} onClick={handleClick}>
          <div className="text-sm font-medium text-white">{data.propertyName || data.unitIdentifier || t.notAvailable}</div>
          <div className="mt-1 text-xs text-white/50">{data.propertyAddress || data.city || t.notAvailable}</div>
        </div>
      );
    default:
      return null;
  }
});

MarketSignalRenderer.displayName = 'MarketSignalRenderer';

